// Reversed FR to EN translations to deal with French values from the schema and convert them to English for the app logic
// Actual schema (in CT):
// https://www.optaservice.com/OptaSingleService/ct?json=simplifiedschema.json

import { isArray, isObject, isString } from "../lib/util";
import getLogger from "../lib/debug-logger";

const log = getLogger("schema-translations", 1);

// the following schema values need to be removed from drop-down options
const removedValues = [
  "Maison mobile",
  "Mobile Home",
  "Parement de métal",
  "Metal Siding",
  "Asphalte résistante aux grêles (Classe 4)",
  "Hail Resistant Asphalt (Class 4)",
];

// the following FR/EN mapping is used to exclude duplicate values from drop-downs
const featFr2En = {
  //ArchitecturalStyleType:
  Détaché: "Detached",
  "Fin de rangeé": "End Row",
  "En rangeé": "In Row",
  "Semi détaché": "Semi Detached",
  Quadruplex: "Fourplex",
  Cinqplex: "Fiveplex",

  //AuxiliaryHeatingType:
  Cheminée: "Fireplace",
  "Poêle à bois": "Woodstove",
  "Fournaise au bois": "Wood Furnace",
  "Poêle à granulés": "Pellet Stove",
  Électrique: "Electric",
  "Pompe à chaleur": "Heat Pump",

  //ExteriorWallType:
  Briques: "Brick",
  "Parement de briques": "Brick Veneer",
  Béton: "Concrete",
  "Revêtement en fibrociment": "Fibreboard",
  //Bois: "Logs", // duplicate - not expected
  Pierre: "Stone",
  "Parement de pierres": "Stone Veneer",
  Stuc: "Stucco",
  "Parement de vinyle": "Vinyl Siding",
  "Parement de bois": "Wood Siding",

  //FoundationType:
  "Sous-sol": "Basement",
  "Vide sanitaire": "Crawlspace",
  Quai: "Pier",
  "Dalle sous-sol": "Slab on Grade",
  "Maison avec sous-sol à entrée": "Walkout Basement",

  //GarageType:
  "Garage attaché": "Attached Garage",
  "Garage integré": "Built-in Garage",
  "Garage au sous-sol": "Basement Garage",
  "Garage détaché": "Detached Garage",

  //PlumbingType:
  Cuivre: "Copper",
  Galvanisé: "Galvanized",
  "Fonte/fer": "Iron",
  ABC: "ABS",

  //PrimaryHeatingType:
  "Chauffage geothermal": "Geothermal",
  "Chauffage radiant": "Radiant",
  Électrique: "Electric",
  "Chauffage au gaz naturel par air pulsé": "Natural Gas Forced Air",
  Mazout: "Oil",
  Eau: "Water",
  Bois: "Wood",
  "Pompe à chaleur": "Heat Pump",

  //ResidentialStoreyCountType:
  "à deux niveaux": "BiLevel",
  "à trois niveaux": "TriLevel",

  //RoofType:
  Asphalte: "Asphalt",
  "Tuiles d'argile": "Clay Tile",
  Béton: "Concrete",
  Cuivre: "Copper",
  Caoutchouc: "Rubber",
  "Tuile ardoise": "Slate Tile",
  Acier: "Steel",
  "Goudron et gravier": "Tar and Gravel",
  Étain: "Tin",
  "Bardeaux de bois": "Wood Shingles",

  //SwimmingPoolType:
  "En béton": "Inground Concrete",
  "Creusée - fibre de verre": "Inground Fibre Glass",
  "Membrane vinyle": "Inground Vinyl",
  "Hors-terre": "Above Ground",
  "Hors-terre - fibre de verre": "Above Ground Fibre Glass",

  // commonValues
  Aucun: "None",
  Inconnu: "Unknown",
  vrai: "true",
  faux: "false",
  Plus: "More",
};

const frTextValues = Object.keys(featFr2En);

const reFrDecimal = /^\d,\d$/; // as used for some construction features - not the general form

// normalize data value: translate from French to English, and fix numeric values with comma as decimal separator
const translateValue = (value) => {
  if (!isString(value)) return value;
  if (reFrDecimal.test(value)) {
    log(`translating numeric value '${value}'`); //---------------- log
    return value.replace(",", ".");
  }
  if (value in featFr2En) {
    const newValue = featFr2En[value];
    log(`translating value '${value}' into '${newValue}'`); //---------------- log
    return newValue;
  }
  return value;
};

export const translateFeatures = (features) => {
  if (!isObject(features)) return features;
  const newFeatures = Object.create(null);
  for (let [k, v] of Object.entries(features)) {
    newFeatures[k] = translateValue(v);
  }
  return newFeatures;
};

// filter off Fr options from an options list, if the corresponding feat has Fr values in the schema
export const filterFeatureOptions = (optsList) => {
  return isArray(optsList)
    ? optsList.filter((opt) => !frTextValues.includes(opt) && !removedValues.includes(opt) && !reFrDecimal.test(opt))
    : optsList;
};
